import React, { useState, useEffect } from 'react';
import { Archive, X, Check, Clipboard, Hand, Plus, Edit2, Trash2, Camera, CornerUpLeft, Upload, Download } from 'lucide-react';
import inventoryService from '../services/inventoryService';
import '../styles/inventory.css';

const InventoryManagement = () => {
  const [activeTab, setActiveTab] = useState('items');
  const [items, setItems] = useState([]);
  const [borrows, setBorrows] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filtreler
  const [filters, setFilters] = useState({
    workshop: '',
    category: '',
    status: '',
    location: '',
    search: ''
  });
  const [borrowStatusFilter, setBorrowStatusFilter] = useState('pending');
  const [locationGroups, setLocationGroups] = useState([]);

  // Modal durumları
  const [showItemModal, setShowItemModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importWorkshop, setImportWorkshop] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, borrow: null, reason: '' });
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showMonthlyCheckModal, setShowMonthlyCheckModal] = useState(false);
  const [photoModal, setPhotoModal] = useState({ open: false, title: '', photos: [] });

  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedBorrow, setSelectedBorrow] = useState(null);

  // Form verileri
  const [itemForm, setItemForm] = useState({
    name: '',
    category: '',
    workshop: '',
    quantity: 1,
    unit: 'Adet',
    condition: 'İyi',
    status: 'Mevcut',
    location: '',
    description: '',
    serialNumber: '',
    purchaseDate: '',
    purchasePrice: 0,
    supplier: '',
    imageUrl: ''
  });

  const [returnForm, setReturnForm] = useState({
    returnPhotos: [],
    returnCondition: 'İyi',
    returnNotes: ''
  });

  // Sabitler
  const WORKSHOPS = [
    "Necmettin Erbakan Teknoloji Atölyesi",
    "Aksaray Hanımlar Binası",
    "Diğer"
  ];

  const CATEGORIES = [
    "Elektronik",
    "El Aletleri",
    "Bilgisayar ve Yazılım",
    "Malzeme",
    "Mobilya",
    "Diğer"
  ];

  const CONDITIONS = ["Yeni", "İyi", "Orta", "Bakım Gerekli", "Arızalı"];
  const STATUSES = ["Mevcut", "Ödünç Verildi", "Bakımda", "Kayıp"];

  const BORROW_STATUS_LABELS = {
    pending: 'Onay Bekliyor',
    active: 'Aktif',
    returned: 'İade Edildi',
    rejected: 'Reddedildi'
  };

  const locationValues = locationGroups.flatMap(g => g.options.map(o => o.value));

  useEffect(() => {
    inventoryService.getLocations()
      .then(res => setLocationGroups(res.groups || []))
      .catch(err => console.error('Konumlar alınamadı:', err));
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab, filters, borrowStatusFilter]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      if (activeTab === 'items') {
        const response = await inventoryService.getAllItems(filters);
        setItems(response.items || []);
      } else if (activeTab === 'borrows') {
        const response = await inventoryService.getAllBorrows(
          borrowStatusFilter ? { status: borrowStatusFilter } : {}
        );
        setBorrows(response.borrows || []);
      } else if (activeTab === 'statistics') {
        const stats = await inventoryService.getStatistics(filters.workshop);
        setStatistics(stats);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============ Öğe İşlemleri ============

  const handleCreateItem = async () => {
    setError('');
    setSuccess('');

    try {
      await inventoryService.createItem(itemForm);
      setSuccess('Öğe başarıyla oluşturuldu');
      setShowItemModal(false);
      resetItemForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateItem = async () => {
    setError('');
    setSuccess('');

    try {
      await inventoryService.updateItem(selectedItem._id, itemForm);
      setSuccess('Öğe başarıyla güncellendi');
      setShowItemModal(false);
      setSelectedItem(null);
      resetItemForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Bu öğeyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await inventoryService.deleteItem(itemId);
      setSuccess('Öğe başarıyla silindi');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const openItemModal = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setItemForm(item);
    } else {
      setSelectedItem(null);
      resetItemForm();
    }
    setShowItemModal(true);
  };

  const resetItemForm = () => {
    setItemForm({
      name: '',
      category: '',
      workshop: '',
      quantity: 1,
      unit: 'Adet',
      condition: 'İyi',
      status: 'Mevcut',
      location: '',
      description: '',
      serialNumber: '',
      purchaseDate: '',
      purchasePrice: 0,
      supplier: '',
      imageUrl: ''
    });
  };

  // ============ Ödünç İşlemleri ============

  const handleApproveBorrow = async (borrow) => {
    setError('');
    setSuccess('');

    try {
      await inventoryService.approveBorrow(borrow._id);
      setSuccess(`${borrow.borrowerName} adlı kişinin talebi onaylandı`);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRejectBorrow = async () => {
    if (!rejectModal.reason.trim()) {
      setError('Lütfen reddetme nedenini yazınız');
      return;
    }
    setError('');
    setSuccess('');

    try {
      await inventoryService.rejectBorrow(rejectModal.borrow._id, rejectModal.reason.trim());
      setSuccess('Talep reddedildi');
      setRejectModal({ open: false, borrow: null, reason: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  // ============ XML İçe Aktarma ============

  const openImportModal = () => {
    setImportFile(null);
    setImportResult(null);
    setImportWorkshop(filters.workshop || '');
    setShowImportModal(true);
  };

  const handleImportXml = async () => {
    if (!importFile) {
      setError('Lütfen bir XML dosyası seçin');
      return;
    }
    setError('');
    setImporting(true);

    try {
      const result = await inventoryService.importItemsXml(importFile, importWorkshop);
      setImportResult(result);
      if (result.imported > 0) loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const downloadXmlTemplate = () => {
    const template = `<?xml version="1.0" encoding="UTF-8"?>
<envanter>
  <urun>
    <ad>Arduino Uno</ad>
    <kategori>Elektronik</kategori>
    <atolye>Necmettin Erbakan Teknoloji Atölyesi</atolye>
    <miktar>10</miktar>
    <birim>Adet</birim>
    <durum>İyi</durum>
    <konum>B2</konum>
    <seri_no></seri_no>
    <aciklama>Başlangıç seti</aciklama>
    <alis_tarihi>2025-01-15</alis_tarihi>
    <alis_fiyati>450</alis_fiyati>
    <tedarikci></tedarikci>
  </urun>
</envanter>
`;
    const blob = new Blob([template], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'envanter_sablon.xml';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleReturnItem = async () => {
    setError('');
    setSuccess('');

    try {
      await inventoryService.returnItem(selectedBorrow._id, returnForm);
      setSuccess('İade işlemi tamamlandı');
      setShowReturnModal(false);
      setSelectedBorrow(null);
      resetReturnForm();
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const openReturnModal = (borrow) => {
    setSelectedBorrow(borrow);
    setShowReturnModal(true);
  };

  const resetReturnForm = () => {
    setReturnForm({
      returnPhotos: [],
      returnCondition: 'İyi',
      returnNotes: ''
    });
  };

  // ============ Fotoğraf Yükleme ============

  const handlePhotoUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const response = await inventoryService.uploadPhoto(file);

      if (type === 'item') {
        setItemForm({ ...itemForm, imageUrl: response.url });
      } else if (type === 'return') {
        setReturnForm({
          ...returnForm,
          returnPhotos: [...returnForm.returnPhotos, response.url]
        });
      }

      setSuccess('Fotoğraf yüklendi');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const removePhoto = (url, type) => {
    if (type === 'return') {
      setReturnForm({
        ...returnForm,
        returnPhotos: returnForm.returnPhotos.filter(p => p !== url)
      });
    }
  };

  const openPhotoModal = (photos, title = 'Fotoğraflar') => {
    if (!photos || photos.length === 0) return;
    setPhotoModal({ open: true, title, photos });
  };

  const closePhotoModal = () => setPhotoModal({ open: false, title: '', photos: [] });

  // ============ Render ============

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <h1><Archive className="inline-icon"/> Envanter Yönetimi</h1>
        <p>Atölye malzemelerini takip edin ve yönetin</p>
      </div>

      {/* Bildirimler */}
      {error && (
        <div className="notification error">
          <X className="inline-icon"/> {error}
          <button onClick={() => setError('')}><X /></button>
        </div>
      )}

      {success && (
        <div className="notification success">
          <Check className="inline-icon"/> {success}
          <button onClick={() => setSuccess('')}><X /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="inventory-tabs">
        <button
          className={activeTab === 'items' ? 'active' : ''}
          onClick={() => setActiveTab('items')}
        >
          <Clipboard className="inline-icon"/> Envanter Listesi
        </button>
        <button
          className={activeTab === 'borrows' ? 'active' : ''}
          onClick={() => setActiveTab('borrows')}
        >
          <Hand className="inline-icon"/> Ödünç İşlemleri
        </button>
        <button
          className={activeTab === 'statistics' ? 'active' : ''}
          onClick={() => setActiveTab('statistics')}
        >
          <Clipboard className="inline-icon"/> İstatistikler
        </button>
      </div>

      {/* İçerik */}
      <div className="inventory-content">
        {activeTab === 'items' && (
          <div className="items-section">
            {/* Filtreler */}
            <div className="filters">
              <select
                value={filters.workshop}
                onChange={(e) => setFilters({ ...filters, workshop: e.target.value })}
              >
                <option value="">Tüm Atölyeler</option>
                {WORKSHOPS.map(w => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>

              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              >
                <option value="">Tüm Kategoriler</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Tüm Durumlar</option>
                {STATUSES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <select
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              >
                <option value="">Tüm Konumlar</option>
                {locationGroups.map(group => (
                  <optgroup key={group.group} label={group.group}>
                    {group.options.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>

              <input
                type="text"
                placeholder="Ara..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />

              <button className="btn-secondary" onClick={openImportModal}>
                <Upload className="inline-icon"/> XML İçe Aktar
              </button>

              <button className="btn-primary" onClick={() => openItemModal()}>
                <Plus className="inline-icon"/> Yeni Öğe
              </button>
            </div>

            {/* Öğeler Tablosu */}
            {loading ? (
              <div className="loading">Yükleniyor...</div>
            ) : (
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Fotoğraf</th>
                    <th>İsim</th>
                    <th>Kategori</th>
                    <th>Atölye</th>
                    <th>Miktar</th>
                    <th>Durum</th>
                    <th>Konum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item._id}>
                      <td data-label="Fotoğraf">
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.name} className="item-thumbnail" />
                        ) : (
                          <div className="no-image"><Archive className="inline-icon"/></div>
                        )}
                      </td>
                      <td data-label="İsim">{item.name}</td>
                      <td data-label="Kategori">{item.category}</td>
                      <td data-label="Atölye">{item.workshop}</td>
                      <td data-label="Miktar">{item.quantity} {item.unit}</td>
                      <td data-label="Durum">
                        <span className={`status-badge ${(item.status || '').toLowerCase().replace(' ', '-')}`}>
                          {item.status}
                        </span>
                      </td>
                      <td data-label="Konum">{item.location || '-'}</td>
                      <td data-label="İşlemler" className="actions">
                        <button onClick={() => openItemModal(item)} title="Düzenle">
                          <Edit2 />
                        </button>
                        <button onClick={() => handleDeleteItem(item._id)} title="Sil">
                          <Trash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'borrows' && (
          <div className="borrows-section">
            <div className="filters">
              <select
                value={borrowStatusFilter}
                onChange={(e) => setBorrowStatusFilter(e.target.value)}
              >
                <option value="pending">Onay Bekleyen Talepler</option>
                <option value="active">Aktif Ödünçler</option>
                <option value="returned">İade Edilenler</option>
                <option value="rejected">Reddedilenler</option>
                <option value="">Tümü</option>
              </select>
            </div>

            {loading ? (
              <div className="loading">Yükleniyor...</div>
            ) : borrows.length === 0 ? (
              <div className="loading">Kayıt bulunamadı</div>
            ) : (
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Öğe</th>
                  <th>Talep Eden</th>
                  <th>İletişim</th>
                  <th>Miktar</th>
                  <th>Talep / Ödünç Tarihi</th>
                  <th>Beklenen İade</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {borrows.map(borrow => (
                  <tr key={borrow._id}>
                    <td data-label="Öğe">{borrow.itemName}</td>
                    <td data-label="Talep Eden">
                      {borrow.borrowerName}
                      {borrow.department && <div className="small-text">{borrow.department}</div>}
                    </td>
                    <td data-label="İletişim">
                      {borrow.borrowerEmail}
                      {borrow.borrowerPhone && <div className="small-text">{borrow.borrowerPhone}</div>}
                    </td>
                    <td data-label="Miktar">{borrow.quantity}</td>
                    <td data-label="Tarih">{new Date(borrow.borrowDate || borrow.createdAt).toLocaleDateString('tr-TR')}</td>
                    <td data-label="Beklenen İade">{new Date(borrow.expectedReturnDate).toLocaleDateString('tr-TR')}</td>
                    <td data-label="Durum">
                      <span className={`status-badge ${borrow.status}`}>
                        {BORROW_STATUS_LABELS[borrow.status] || borrow.status}
                      </span>
                      {borrow.notes && <div className="small-text">Not: {borrow.notes}</div>}
                      {borrow.rejectionReason && <div className="small-text">Sebep: {borrow.rejectionReason}</div>}
                    </td>
                    <td data-label="İşlemler" className="actions">
                      {borrow.status === 'pending' && (
                        <>
                          <button onClick={() => handleApproveBorrow(borrow)} title="Onayla">
                            <Check className="inline-icon"/> Onayla
                          </button>
                          <button
                            onClick={() => setRejectModal({ open: true, borrow, reason: '' })}
                            title="Reddet"
                          >
                            <X className="inline-icon"/> Reddet
                          </button>
                        </>
                      )}
                      {borrow.status === 'active' && (
                        <button onClick={() => openReturnModal(borrow)} title="İade Et">
                          <CornerUpLeft className="inline-icon"/> İade
                        </button>
                      )}
                      {borrow.borrowPhotos && borrow.borrowPhotos.length > 0 && (
                        <button
                          title="Fotoğraflar"
                          onClick={() => openPhotoModal(borrow.borrowPhotos, `Fotoğraflar - ${borrow.itemName}`)}
                        >
                          <Camera />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        )}

        {activeTab === 'statistics' && statistics && (
          <div className="statistics-section">
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Toplam Öğe</h3>
                <div className="stat-value">{statistics.totalItems}</div>
              </div>
              <div className="stat-card">
                <h3>Aktif Ödünçler</h3>
                <div className="stat-value">{statistics.activeBorrows}</div>
              </div>
              <div className="stat-card">
                <h3>Gecikmiş İadeler</h3>
                <div className="stat-value">{statistics.overdueBorrows}</div>
              </div>
            </div>

            <div className="charts">
              <div className="chart-card">
                <h3>Kategoriye Göre Dağılım</h3>
                <div className="category-list">
                  {statistics.categoryDistribution?.map(cat => (
                    <div key={cat._id} className="category-item">
                      <span>{cat._id}</span>
                      <span className="category-count">{cat.count} öğe</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-card">
                <h3>Durum Dağılımı</h3>
                <div className="status-list">
                  {statistics.statusDistribution?.map(status => (
                    <div key={status._id} className="status-item">
                      <span>{status._id}</span>
                      <span className="status-count">{status.count} öğe</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Öğe Modal */}
      {showItemModal && (
        <div className="modal-overlay" onClick={() => setShowItemModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedItem ? 'Öğeyi Düzenle' : 'Yeni Öğe Ekle'}</h2>
              <button onClick={() => setShowItemModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>İsim *</label>
                  <input
                    type="text"
                    value={itemForm.name}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Kategori *</label>
                  <select
                    value={itemForm.category}
                    onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                    required
                  >
                    <option value="">Seçin</option>
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Atölye *</label>
                  <select
                    value={itemForm.workshop}
                    onChange={(e) => setItemForm({ ...itemForm, workshop: e.target.value })}
                    required
                  >
                    <option value="">Seçin</option>
                    {WORKSHOPS.map(w => (
                      <option key={w} value={w}>{w}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Miktar *</label>
                  <input
                    type="number"
                    value={itemForm.quantity}
                    onChange={(e) => setItemForm({ ...itemForm, quantity: parseInt(e.target.value) })}
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Birim</label>
                  <input
                    type="text"
                    value={itemForm.unit}
                    onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Durum</label>
                  <select
                    value={itemForm.condition}
                    onChange={(e) => setItemForm({ ...itemForm, condition: e.target.value })}
                  >
                    {CONDITIONS.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Konum</label>
                  <select
                    value={itemForm.location || ''}
                    onChange={(e) => setItemForm({ ...itemForm, location: e.target.value })}
                  >
                    <option value="">Seçin</option>
                    {itemForm.location && !locationValues.includes(itemForm.location) && (
                      <option value={itemForm.location}>{itemForm.location} (eski kayıt)</option>
                    )}
                    {locationGroups.map(group => (
                      <optgroup key={group.group} label={group.group}>
                        {group.options.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Seri No</label>
                  <input
                    type="text"
                    value={itemForm.serialNumber}
                    onChange={(e) => setItemForm({ ...itemForm, serialNumber: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Alış Tarihi</label>
                  <input
                    type="date"
                    value={itemForm.purchaseDate}
                    onChange={(e) => setItemForm({ ...itemForm, purchaseDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Alış Fiyatı (TL)</label>
                  <input
                    type="number"
                    value={itemForm.purchasePrice}
                    onChange={(e) => setItemForm({ ...itemForm, purchasePrice: parseFloat(e.target.value) })}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label>Tedarikçi</label>
                  <input
                    type="text"
                    value={itemForm.supplier}
                    onChange={(e) => setItemForm({ ...itemForm, supplier: e.target.value })}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Açıklama</label>
                  <textarea
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    rows="3"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Fotoğraf</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload(e, 'item')}
                  />
                  {itemForm.imageUrl && (
                    <img src={itemForm.imageUrl} alt="Preview" className="image-preview" />
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowItemModal(false)}>
                İptal
              </button>
              <button
                className="btn-primary"
                onClick={selectedItem ? handleUpdateItem : handleCreateItem}
              >
                {selectedItem ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* XML İçe Aktarma Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>XML ile Toplu Veri Girişi</h2>
              <button onClick={() => setShowImportModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <p className="small-text">
                Her malzeme bir kayıt olacak şekilde XML dosyası yükleyin. Alan adları: ad, kategori, atolye,
                miktar, birim, durum, konum (örn. B2), seri_no, aciklama, alis_tarihi, alis_fiyati, tedarikci.
                Excel'den "XML Elektronik Tablo 2003" olarak kaydedilen dosyalar da desteklenir (ilk satır başlık olmalı).
                Daha önce eklenmiş kayıtlar tekrar eklenmez.
              </p>
              <button className="btn-secondary" onClick={downloadXmlTemplate}>
                <Download className="inline-icon"/> Örnek XML Şablonu İndir
              </button>

              <div className="form-grid" style={{ marginTop: 16 }}>
                <div className="form-group">
                  <label>Varsayılan Atölye</label>
                  <select value={importWorkshop} onChange={(e) => setImportWorkshop(e.target.value)}>
                    <option value="">Dosyadaki atölye kullanılsın</option>
                    {WORKSHOPS.map(w => (
                      <option key={w} value={w}>{w}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>XML Dosyası *</label>
                  <input
                    type="file"
                    accept=".xml,application/xml,text/xml"
                    onChange={(e) => {
                      setImportFile(e.target.files[0] || null);
                      setImportResult(null);
                    }}
                  />
                </div>
              </div>

              {importResult && (
                <div className="borrow-info">
                  <p>
                    <strong>{importResult.total}</strong> kayıttan <strong>{importResult.imported}</strong> tanesi eklendi
                    {importResult.skipped.length > 0 && `, ${importResult.skipped.length} tanesi zaten kayıtlı olduğu için atlandı`}
                    {importResult.errors.length > 0 && `, ${importResult.errors.length} tanesi hatalı`}.
                  </p>
                  {importResult.unknownFields.length > 0 && (
                    <p className="small-text">Tanınmayan alanlar (yok sayıldı): {importResult.unknownFields.join(', ')}</p>
                  )}
                  {[...importResult.errors, ...importResult.warnings].length > 0 && (
                    <ul className="small-text" style={{ maxHeight: 180, overflowY: 'auto' }}>
                      {importResult.errors.map((line, i) => <li key={`e${i}`} style={{ color: '#c62828' }}>{line}</li>)}
                      {importResult.warnings.map((line, i) => <li key={`w${i}`}>{line}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowImportModal(false)}>
                Kapat
              </button>
              <button className="btn-primary" onClick={handleImportXml} disabled={importing || !importFile}>
                {importing ? 'Aktarılıyor...' : 'İçe Aktar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Talep Reddetme Modal */}
      {rejectModal.open && rejectModal.borrow && (
        <div className="modal-overlay" onClick={() => setRejectModal({ open: false, borrow: null, reason: '' })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Talebi Reddet: {rejectModal.borrow.itemName}</h2>
              <button onClick={() => setRejectModal({ open: false, borrow: null, reason: '' })}>×</button>
            </div>

            <div className="modal-body">
              <p><strong>Talep Eden:</strong> {rejectModal.borrow.borrowerName} ({rejectModal.borrow.borrowerEmail})</p>
              <div className="form-group full-width">
                <label>Reddetme Nedeni *</label>
                <textarea
                  value={rejectModal.reason}
                  onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
                  rows="3"
                  placeholder="Talep eden kişiye bildirim olarak iletilecek"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setRejectModal({ open: false, borrow: null, reason: '' })}>
                İptal
              </button>
              <button className="btn-primary" onClick={handleRejectBorrow}>
                Reddet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* İade Modal */}
      {showReturnModal && selectedBorrow && (
        <div className="modal-overlay" onClick={() => setShowReturnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>İade: {selectedBorrow.itemName}</h2>
              <button onClick={() => setShowReturnModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="borrow-info">
                <p><strong>Ödünç Alan:</strong> {selectedBorrow.borrowerName}</p>
                <p><strong>Miktar:</strong> {selectedBorrow.quantity}</p>
                <p><strong>Ödünç Tarihi:</strong> {new Date(selectedBorrow.borrowDate).toLocaleDateString('tr-TR')}</p>
                <p><strong>Ödünç Alırken Durum:</strong> {selectedBorrow.borrowCondition}</p>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>İade Durumu</label>
                  <select
                    value={returnForm.returnCondition}
                    onChange={(e) => setReturnForm({ ...returnForm, returnCondition: e.target.value })}
                  >
                    {CONDITIONS.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>İade Notu</label>
                  <textarea
                    value={returnForm.returnNotes}
                    onChange={(e) => setReturnForm({ ...returnForm, returnNotes: e.target.value })}
                    rows="2"
                    placeholder="Varsa hasarlar veya notlar..."
                  />
                </div>

                <div className="form-group full-width">
                  <label>İade Fotoğrafları</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoUpload(e, 'return')}
                    multiple
                  />
                  <div className="photo-grid">
                    {returnForm.returnPhotos.map((url, index) => (
                      <div key={index} className="photo-item">
                        <img src={url} alt={`Return ${index + 1}`} />
                        <button onClick={() => removePhoto(url, 'return')}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Ödünç alırken çekilen fotoğraflar */}
              {selectedBorrow.borrowPhotos && selectedBorrow.borrowPhotos.length > 0 && (
                <div className="borrow-photos-section">
                  <h4>Ödünç Alırken Çekilen Fotoğraflar</h4>
                  <div className="photo-grid">
                    {selectedBorrow.borrowPhotos.map((url, index) => (
                      <div key={index} className="photo-item">
                        <img src={url} alt={`Borrow ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowReturnModal(false)}>
                İptal
              </button>
              <button className="btn-primary" onClick={handleReturnItem}>
                İade Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

      {photoModal.open && (
        <div className="modal-overlay" onClick={closePhotoModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{photoModal.title}</h2>
              <button onClick={closePhotoModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="photo-grid">
                {photoModal.photos.map((url, index) => (
                  <div key={index} className="photo-item">
                    <img src={url} alt={`Photo ${index + 1}`} />
                    <a href={url} target="_blank" rel="noopener noreferrer" className="open-new-tab">
                      Yeni sekmede aç
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;
